/**
 * TagFilter Component
 * Dropdown for filtering links by tags
 */

import { useState, useEffect, useRef } from "react";
import { Effect } from "effect";
import { ChevronDown, X, Tag, Check } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { getAllTags } from "../services/tagService";
import type { Tag as TagType } from "../types/link";

interface TagFilterProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export function TagFilter({ selectedTags, onTagsChange }: TagFilterProps) {
  const [allTags, setAllTags] = useState<TagType[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      try {
        const tags = await Effect.runPromise(getAllTags());
        setAllTags(tags as TagType[]);
      } catch {
        console.error("Failed to load tags");
      }
      setLoading(false);
    };
    fetchTags();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTag = (tagName: string) => {
    if (selectedTags.includes(tagName)) {
      onTagsChange(selectedTags.filter((t) => t !== tagName));
    } else {
      onTagsChange([...selectedTags, tagName]);
    }
  };

  const clearTags = () => {
    onTagsChange([]);
    setOpen(false);
  };

  if (loading || allTags.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <div ref={dropdownRef} className="relative">
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1"
          onClick={() => setOpen(!open)}
        >
          <Tag className="h-4 w-4" />
          태그 필터
          {selectedTags.length > 0 && (
            <Badge variant="secondary" className="ml-1 rounded-full px-1.5">
              {selectedTags.length}
            </Badge>
          )}
          <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </Button>

        {open && (
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[180px] rounded-md border bg-popover p-1 shadow-md">
            <div className="max-h-60 overflow-y-auto">
              {allTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.name)}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                >
                  <div className={`flex h-4 w-4 items-center justify-center rounded border ${
                    selectedTags.includes(tag.name)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input"
                  }`}>
                    {selectedTags.includes(tag.name) && (
                      <Check className="h-3 w-3" />
                    )}
                  </div>
                  <span>{tag.name}</span>
                </button>
              ))}
            </div>
            {selectedTags.length > 0 && (
              <div className="mt-1 border-t pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center text-xs"
                  onClick={clearTags}
                >
                  필터 초기화
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected tags badges */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedTags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="gap-1 pr-1"
            >
              {tag}
              <button
                type="button"
                onClick={() => toggleTag(tag)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
